import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';

/**
 * Sends a document (invoice/dunning PDF) by e-mail via the configured SMTP server.
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   defaultTo?: string,
 *   subject: string,
 *   buildAttachment: () => Promise<{ base64: string, name: string }>,  // built lazily on send (jsPDF loads lazily)
 * }} props
 */
export function SendEmailModal({ open, onClose, defaultTo, subject, buildAttachment }) {
  const { t } = useTranslation();
  const [to, setTo] = useState('');

  useEffect(() => {
    if (open) setTo(defaultTo || '');
  }, [open, defaultTo]);

  const send = useMutation({
    mutationFn: async () => {
      const att = await buildAttachment();
      return financeApi.sendEmail({
        to,
        subject,
        text: t('email.body'),
        attachmentBase64: att.base64,
        attachmentName: att.name,
      });
    },
    onSuccess: () => onClose(),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('email.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={send.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={() => send.mutate()} disabled={send.isPending || !to}>
            {send.isPending ? `${t('email.send')}…` : t('email.send')}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <p className="muted text-sm" style={{ margin: 0 }}>{t('email.intro')}</p>
        <label className="field">
          <span className="field-label">{t('email.to')}</span>
          <input className="input" type="email" value={to} onChange={(e) => setTo(e.target.value)} required />
        </label>
        <div className="muted text-sm">
          {t('email.subject')}: <strong>{subject}</strong>
        </div>
        {send.isError && <div className="alert danger">{send.error?.message || t('common.loadError')}</div>}
      </div>
    </Modal>
  );
}
